import type { AudioFramePayload } from "./aiHub/types";
import type { CanonicalKey } from "./aiHub/types";
import { clamp01 } from "./aiHub/rhythmAudio";

export const CLASSIC_SIDE_BUFFER_COL_COUNT = 4;

export type RhythmLayoutTopology = {
  orderedCols: number[];
  orderedRows: number[];
  colToBandIndex: Map<number, number>;
  rowToIndex: Map<number, number>;
  minCol: number;
  maxCol: number;
  minRow: number;
  maxRow: number;
  bandCount: number;
  bufferLeftCols: Set<number>;
  bufferRightCols: Set<number>;
  mainCols: number[];
  hasNumpad: boolean;
  /** 频谱瀑布阶梯起点列（小键盘 Enter / . 或最右列） */
  waterfallOriginCol: number;
  waterfallOriginRow: number;
};

const NUMPAD_ORIGIN_CODES = ["NumpadEnter", "NumpadDecimal"] as const;

export function buildRhythmLayoutTopology(
  keys: CanonicalKey[]
): RhythmLayoutTopology {
  const colSet = new Set<number>();
  const rowSet = new Set<number>();
  let hasNumpad = false;

  for (const key of keys) {
    if (key.isGap) continue;
    colSet.add(key.col);
    rowSet.add(key.row);
    if (key.uuid.startsWith("Numpad")) hasNumpad = true;
  }

  const orderedCols = Array.from(colSet).sort((a, b) => a - b);
  const orderedRows = Array.from(rowSet).sort((a, b) => a - b);
  const minCol = orderedCols[0] ?? 0;
  const maxCol = orderedCols[orderedCols.length - 1] ?? 0;
  const minRow = orderedRows[0] ?? 0;
  const maxRow = orderedRows[orderedRows.length - 1] ?? 0;

  const colToBandIndex = new Map<number, number>();
  orderedCols.forEach((col, index) => colToBandIndex.set(col, index));

  const rowToIndex = new Map<number, number>();
  orderedRows.forEach((row, index) => rowToIndex.set(row, index));

  const bufferLeftCols = new Set(
    orderedCols.slice(
      0,
      Math.min(CLASSIC_SIDE_BUFFER_COL_COUNT, orderedCols.length)
    )
  );
  const bufferRightCols = new Set(
    orderedCols.slice(
      Math.max(0, orderedCols.length - CLASSIC_SIDE_BUFFER_COL_COUNT)
    )
  );
  const mainCols = orderedCols.filter(
    (col) => !bufferLeftCols.has(col) && !bufferRightCols.has(col)
  );

  let waterfallOriginCol = maxCol;
  let waterfallOriginRow = maxRow;
  for (const code of NUMPAD_ORIGIN_CODES) {
    const anchor = keys.find((k) => !k.isGap && k.uuid === code);
    if (anchor) {
      waterfallOriginCol = anchor.col;
      waterfallOriginRow = anchor.row;
      break;
    }
  }

  return {
    orderedCols,
    orderedRows,
    colToBandIndex,
    rowToIndex,
    minCol,
    maxCol,
    minRow,
    maxRow,
    bandCount: Math.max(1, orderedCols.length),
    bufferLeftCols,
    bufferRightCols,
    mainCols,
    hasNumpad,
    waterfallOriginCol,
    waterfallOriginRow,
  };
}

/** 按实际列数从 32-bin 频谱取每列能量（0~1） */
export function computeLayoutColumnLevels(
  frame: AudioFramePayload,
  topology: RhythmLayoutTopology
): Map<number, number> {
  const levels = new Map<number, number>();
  const n = frame.spectrum.length;
  const beatBoost = frame.beat ? 0.14 : 0;
  const rmsLift = 0.16 + frame.rms * 0.62;
  const bandCount = topology.bandCount;

  for (const col of topology.orderedCols) {
    const band = topology.colToBandIndex.get(col) ?? 0;
    const start = Math.floor((band / bandCount) * n);
    const end = Math.max(start + 1, Math.ceil(((band + 1) / bandCount) * n));
    let peak = 0;
    let sum = 0;
    for (let i = start; i < end; i += 1) {
      const value = frame.spectrum[i] ?? 0;
      sum += value;
      peak = Math.max(peak, value);
    }
    const avg = sum / Math.max(1, end - start);
    const mixed = avg * 0.52 + peak * 0.48;
    levels.set(col, clamp01(Math.pow(mixed, 0.62) * rmsLift + beatBoost));
  }

  return levels;
}

/** 频谱瀑布：从起点列向右列 spread=0，向左递增 */
export function waterfallBandIndexForCol(
  col: number,
  topology: RhythmLayoutTopology
): number {
  const originIdx =
    topology.colToBandIndex.get(topology.waterfallOriginCol) ??
    topology.bandCount - 1;
  const colIdx = topology.colToBandIndex.get(col) ?? 0;
  return Math.max(0, originIdx - colIdx);
}

export function columnLevelsToArray(
  topology: RhythmLayoutTopology,
  levels: Map<number, number>
): number[] {
  return topology.orderedCols.map((col) => levels.get(col) ?? 0);
}

export function isClassicBufferCol(
  col: number,
  topology: RhythmLayoutTopology
): boolean {
  return topology.bufferLeftCols.has(col) || topology.bufferRightCols.has(col);
}

export function bottomRowIndex(
  key: CanonicalKey,
  topology: RhythmLayoutTopology
): number {
  return topology.maxRow - key.row;
}

export function topRowIndex(
  key: CanonicalKey,
  topology: RhythmLayoutTopology
): number {
  return topology.rowToIndex.get(key.row) ?? 0;
}
